import React, { useState, useEffect } from 'react';
import { Search, RefreshCw, AlertCircle } from 'lucide-react';

export default function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  const fetchLogs = async (showIndicator = false) => {
    if (showIndicator) setRefreshing(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/admin/audit-logs', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Erreur lors du chargement des logs d\'audit');
      }

      const data = await response.json();
      setLogs(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  const filteredLogs = logs.filter(log => 
    log.action.toLowerCase().includes(search.toLowerCase()) ||
    log.admin.email.toLowerCase().includes(search.toLowerCase()) ||
    log.targetType.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return <div style={styles.loadingContainer}>Chargement des journaux d'audit...</div>;
  }

  if (error) {
    return (
      <div style={styles.errorContainer}>
        <AlertCircle size={32} color="var(--danger)" />
        <p style={{ marginTop: '12px' }}>{error}</p>
        <button onClick={() => fetchLogs(true)} style={{ marginTop: '12px' }} className="btn-secondary">
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.headerRow}>
        <span style={styles.subtitle}>Journal d'audit immuable des actions de sécurité de la plateforme</span>
        <button 
          onClick={() => fetchLogs(true)} 
          disabled={refreshing} 
          style={styles.refreshBtn}
          className="btn-secondary"
        >
          <RefreshCw size={14} className={refreshing ? 'spin-anim' : ''} />
          <span>{refreshing ? 'Actualisation...' : 'Actualiser'}</span>
        </button>
      </div>

      <div style={styles.filterBar} className="glass-card">
        <Search size={16} style={styles.searchIcon} />
        <input 
          type="text" 
          placeholder="Filtrer par action, admin ou cible..." 
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={styles.searchInput}
          className="input-field"
        />
      </div>

      <div style={styles.tablePanel} className="glass-card">
        {filteredLogs.length === 0 ? (
          <div style={styles.emptyTable}>Aucune entrée d'audit trouvée.</div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Horodatage</th>
                <th>Administrateur</th>
                <th>Action</th>
                <th>Cible Type</th>
                <th>Cible ID</th>
                <th>Détails</th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs.map((log) => (
                <tr key={log.id}>
                  <td style={styles.timeCell}>
                    {new Date(log.createdAt).toLocaleString()}
                  </td>
                  <td style={styles.adminCell}>{log.admin.email}</td>
                  <td>
                    <span style={{ 
                      ...styles.actionBadge,
                      backgroundColor: log.action.includes('approve') || log.action.includes('reactivate') ? 'var(--success-bg)' :
                                       log.action.includes('reject') || log.action.includes('suspend') ? 'var(--danger-bg)' : 'rgba(59, 130, 246, 0.08)',
                      color: log.action.includes('approve') || log.action.includes('reactivate') ? 'var(--success)' :
                             log.action.includes('reject') || log.action.includes('suspend') ? 'var(--danger)' : 'var(--primary)',
                    }}>
                      {log.action}
                    </span>
                  </td>
                  <td>{log.targetType}</td>
                  <td style={styles.monoCell} title={log.targetId}>
                    {log.targetId.substring(0,8)}...
                  </td>
                  <td style={styles.metadataCell}>{log.metadata || 'Aucun détail'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    animation: 'fadeIn 0.4s ease',
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: 'var(--text-secondary)',
    fontFamily: 'var(--font-title)',
    fontSize: '1.1rem',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    padding: '40px',
    textAlign: 'center',
    color: 'var(--text-secondary)',
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  subtitle: {
    fontSize: '0.9rem',
    color: 'var(--text-secondary)',
  },
  refreshBtn: {
    padding: '8px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '0.85rem',
  },
  filterBar: {
    padding: '12px 16px',
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
  },
  searchIcon: {
    position: 'absolute',
    left: '28px',
    color: 'var(--text-muted)',
  },
  searchInput: {
    paddingLeft: '40px',
  },
  tablePanel: {
    overflowX: 'auto',
  },
  emptyTable: {
    padding: '40px',
    textAlign: 'center',
    color: 'var(--text-secondary)',
  },
  timeCell: {
    fontSize: '0.8rem',
    color: 'var(--text-secondary)',
    whiteSpace: 'nowrap',
  },
  adminCell: {
    fontWeight: '600',
  },
  actionBadge: {
    fontSize: '0.75rem',
    fontWeight: '600',
    padding: '2px 8px',
    borderRadius: '4px',
    textTransform: 'uppercase',
  },
  monoCell: {
    fontFamily: 'monospace',
    fontSize: '0.8rem',
    color: 'var(--text-secondary)',
  },
  metadataCell: {
    fontSize: '0.85rem',
    color: 'var(--text-secondary)',
    maxWidth: '280px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  }
};
